import type { ApiError } from "@/types/api";

export class HttpError extends Error implements ApiError {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

/** Browser network/CORS/offline errors surface as TypeError with this message. */
export function formatFetchError(err: unknown, apiBaseUrl: string): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (
    err instanceof TypeError ||
    /failed to fetch/i.test(msg) ||
    /networkerror/i.test(msg) ||
    /load failed/i.test(msg)
  ) {
    const local =
      /localhost|127\.0\.0\.1/i.test(apiBaseUrl) ? " Start the intake BFF (func start in R1IntakeFunctions)." : "";
    return `Could not reach the API at ${apiBaseUrl}.${local} Check your connection or try again in a moment.`;
  }
  return msg;
}

function joinUrl(base: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const b = base.endsWith("/") ? base.slice(0, -1) : base;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

export function createHttp(baseUrl: string) {
  async function request<T>(
    path: string,
    init?: RequestInit & { json?: unknown },
  ): Promise<T> {
    const { json, headers, ...rest } = init ?? {};
    let res: Response;
    try {
      res = await fetch(joinUrl(baseUrl, path), {
        ...rest,
        headers: {
          Accept: "application/json",
          ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
          ...headers,
        },
        body: json !== undefined ? JSON.stringify(json) : rest.body,
      });
    } catch (err) {
      throw new HttpError(0, formatFetchError(err, baseUrl));
    }

    if (!res.ok) {
      let message = `Request failed (HTTP ${res.status})`;
      try {
        const data = (await res.json()) as { error?: string; message?: string };
        message = data.message || data.error || message;
      } catch {
        /* non-JSON error body */
      }
      throw new HttpError(res.status, message);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    get: <T>(path: string) => request<T>(path, { method: "GET" }),
    post: <T>(path: string, json: unknown) => request<T>(path, { method: "POST", json }),
  };
}
